import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import ClipLoader from 'react-spinners/ClipLoader';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const normalizeId = (id) => (typeof id === 'object' ? id._id : id);

const WebstoreAdminChat = () => {
  const socketRef = useRef(null);
  const selectedUserRef = useRef(null);
  const drawerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatBodyRef = useRef(null);
  const typingTimeout = useRef(null); // This is correctly declared here

  const [users, setUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUserId, setTypingUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [unreadMap, setUnreadMap] = useState({});
  const [drawerVisible, setDrawerVisible] = useState(true);
  const [readStatus, setReadStatus] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(true);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();

    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = yesterday.toDateString() === date.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (isYesterday) {
      return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  useEffect(() => {
    if (!socketRef.current) {
      const socket = io(API_BASE_URL, { withCredentials: true });
      socketRef.current = socket;

      socket.emit('join', { isAdmin: true });

      socket.on('user-online', (onlineIds) => {
        setOnlineUsers(onlineIds);
      });

      socket.on('typing', ({ userId, isTyping }) => {
        if (normalizeId(selectedUserRef.current?._id) === normalizeId(userId)) {
          setTypingUserId(isTyping ? userId : null);
        }
      });

      socket.on('message-read-confirmed', ({ userId }) => {
        if (normalizeId(selectedUserRef.current?._id) === normalizeId(userId)) {
          setReadStatus((prev) => ({ ...prev, [userId]: true }));
        }
      });

      fetchUsersAndLastMessages();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleNewMessage = (msg) => {
      const userId = normalizeId(msg.user);
      const createdAt = msg.createdAt || new Date().toISOString();
      const selectedId = normalizeId(selectedUserRef.current?._id);

      if (selectedId && selectedId === userId) {
        setMessages((prev) => [...prev, msg]);
        if (msg.sender === 'user') {
          socket.emit('mark-read', { userId });
        }
      } else {
        setUnreadMap((prev) => ({
          ...prev,
          [userId]: (prev[userId] || 0) + 1,
        }));
      }

      setUsers((prevUsers) => {
        const updated = [...prevUsers];
        const index = updated.findIndex((u) => normalizeId(u._id) === userId);

        if (index >= 0) {
          updated[index] = {
            ...updated[index],
            latestMessage: createdAt,
          };
        } else {
          updated.push({
            _id: userId,
            username: `User ${userId.substring(0, 4)}...`,
            latestMessage: createdAt,
          });
        }

        return updated.sort(
          (a, b) => new Date(b.latestMessage || 0) - new Date(a.latestMessage || 0)
        );
      });
    };

    socket.off('new-message', handleNewMessage);
    socket.on('new-message', handleNewMessage);

    return () => {
      socket.off('new-message', handleNewMessage);
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleHistory = (history) => {
      const currentUser = selectedUserRef.current;
      if (!currentUser) return;

      setMessages(history);

      const lastMsgTime = history.length
        ? history[history.length - 1].createdAt
        : new Date().toISOString();

      setUsers((prevUsers) => {
        const updated = prevUsers.map((u) =>
          normalizeId(u._id) === normalizeId(currentUser._id)
            ? { ...u, latestMessage: lastMsgTime }
            : u
        );

        return updated.sort(
          (a, b) => new Date(b.latestMessage || 0) - new Date(a.latestMessage || 0)
        );
      });

      socket.emit('mark-read', { userId: currentUser._id });
    };

    socket.off('chat-history', handleHistory);
    socket.on('chat-history', handleHistory);

    return () => socket.off('chat-history', handleHistory);
  }, []);

  const fetchUsersAndLastMessages = async () => {
    try {
      setLoadingUsers(true);
      const [usersRes, lastMsgsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/webstore-users`, {
          headers: { 'x-admin-auth': 'navarrojuben' },
        }),
        axios.get(`${API_BASE_URL}/api/webstore-chat/admin/latest-messages`, {
          headers: { 'x-admin-auth': 'navarrojuben' },
        }),
      ]);

      const lastMap = lastMsgsRes.data;

      const enrichedUsers = usersRes.data.map((u) => ({
        ...u,
        latestMessage: lastMap[u._id] || new Date(0).toISOString(),
      }));

      enrichedUsers.sort((a, b) => {
        const aOnline = onlineUsers.includes(normalizeId(a._id));
        const bOnline = onlineUsers.includes(normalizeId(b._id));

        if (aOnline && !bOnline) return -1;
        if (!aOnline && bOnline) return 1;

        return new Date(b.latestMessage).getTime() - new Date(a.latestMessage).getTime();
      });

      setUsers(enrichedUsers);
    } catch (err) {
      console.error('❌ Failed to fetch users or last messages:', err.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  const selectUser = (user) => {
    setSelectedUser(user);
    selectedUserRef.current = user;

    setDrawerVisible(false);

    setUnreadMap((prev) => {
      const updated = { ...prev };
      delete updated[normalizeId(user._id)];
      return updated;
    });

    setTypingUserId(null);

    const socket = socketRef.current;
    if (!socket) return;

    socket.emit('get-history', { userId: user._id });
  };

  const handleSend = () => {
    if (!newMsg.trim() || !selectedUser) return;

    const msgData = {
      userId: selectedUser._id,
      message: newMsg.trim(),
      sender: 'admin',
    };

    const socket = socketRef.current;
    if (socket) {
      socket.emit('send-message', msgData);
    }

    setUsers((prevUsers) => {
      const now = new Date().toISOString();
      const updated = [...prevUsers];
      const index = updated.findIndex(
        (u) => normalizeId(u._id) === normalizeId(selectedUser._id)
      );

      if (index >= 0) {
        updated[index] = {
          ...updated[index],
          latestMessage: now,
        };
      }

      return updated.sort(
        (a, b) => new Date(b.latestMessage || 0) - new Date(a.latestMessage || 0)
      );
    });

    setReadStatus((prev) => ({
      ...prev,
      [normalizeId(selectedUser._id)]: false,
    }));

    setNewMsg('');
  };

  const handleTyping = useCallback(() => {
    if (!selectedUser) return;

    const socket = socketRef.current;
    if (!socket) return;

    socket.emit('typing', {
      userId: selectedUser._id,
      isTyping: true,
    });

    // Access typingTimeout through its .current property
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket.emit('typing', {
        userId: selectedUser._id,
        isTyping: false,
      });
    }, 1000);
  }, [selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setDrawerVisible(false);
      }
    };

    if (drawerVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [drawerVisible]);

  return (
    <div className="webstore-admin-chat-container">
      <div className={`chat-drawer ${drawerVisible ? '' : 'hidden'}`} ref={drawerRef}>
        <div className="drawer-header">
          <button className="scroll-top-btn" onClick={() => {
             if (chatBodyRef.current) {
                chatBodyRef.current.scrollTop = 0;
             } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
             }
          }}>
            <ArrowUpwardIcon fontSize="small" />
          </button>
          <h3>Messages</h3>
          <button className="hide-btn" onClick={() => setDrawerVisible(false)}>←</button>
        </div>

        {loadingUsers ? (
          <div className="drawer-loading">
            <ClipLoader color="#36d7b7" size={30} />
          </div>
        ) : (
          <ul>
            {users.map((u) => (
              <li
                key={u._id}
                className={selectedUser?._id === u._id ? 'selected' : ''}
                onClick={() => selectUser(u)}
              >
                {u.username}
                {onlineUsers.includes(normalizeId(u._id)) && <span className="online-badge" />}
                {unreadMap[u._id] > 0 && (
                  <span className="unread-badge">{unreadMap[u._id]}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="chat-main">
        <button
          className="drawer-toggle"
          onClick={() => setDrawerVisible(true)}
          style={{ display: drawerVisible ? 'none' : 'block' }}
        >
          <ChatBubbleOutlineIcon />
        </button>


        {selectedUser ? (
          <>
            <div className="chat-header">
              {selectedUser.username}
              {onlineUsers.includes(normalizeId(selectedUser._id)) && (
                <span className="online-dot" />
              )}
            </div>

            <div className="chat-body" ref={chatBodyRef}>
              {messages.map((msg, idx) => {
                const lastAdminMsgIndex = [...messages].map(m => m.sender).lastIndexOf('admin');
                const isLastAdminMessage = msg.sender === 'admin' && idx === lastAdminMsgIndex;

                return (
                  <div
                    key={msg._id || idx}
                    className={`chat-message ${msg.sender === 'admin' ? 'admin' : 'user'}`}
                  >
                    <div className="chat-bubble">
                      {msg.message && <p className="chat-text">{msg.message}</p>}
                      {msg.imageUrl && (
                        <div className="chat-image-container">
                          <img src={msg.imageUrl} alt="Sent" className="chat-image" />
                        </div>
                      )}
                      <div className="chat-meta">
                        <span className="chat-time">
                          {formatTimestamp(msg.createdAt)}
                        </span>
                      </div>
                    </div>
                    {isLastAdminMessage && readStatus[normalizeId(selectedUser._id)] && <div className="chat-seen-indicator">✓✓ Seen</div>}
                  </div>
                );
              })}

              {typingUserId === selectedUser._id && (
                <div className="typing-indicator left">
                  <div className="typing-bubble">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input">
              <input
                type="text"
                placeholder="Type a message..."
                value={newMsg}
                onChange={(e) => {
                  setNewMsg(e.target.value);
                  handleTyping();
                }}
                onKeyDown={(e) => e.key === 'Enter' && newMsg.trim() && handleSend()}
              />
              <button onClick={handleSend} disabled={!newMsg.trim()}>Send</button>
            </div>
          </>
        ) : (
          <div className="chat-placeholder">Select a user to start chatting.</div>
        )}
      </div>
    </div>
  );
};

export default WebstoreAdminChat;